import { GraphQLResult } from '@aws-amplify/api';
import { API, Auth, graphqlOperation } from 'aws-amplify';
import { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth';
import axios from 'axios';
import API_ENDPOINTS from '../../../apiEndpoints';
import { getCognitoUserSessions, loginCognitoUser, logoutCognitoUser } from '../../../graphql';
import type {
  GetCognitoUserSessionsQuery,
  LoginCognitoUserMutation,
  LoginCognitoUserMutationVariables,
  LogoutCognitoUserMutation,
} from '../../../graphql/API';

export type CognitoUserSession = NonNullable<
  NonNullable<GetCognitoUserSessionsQuery['getCognitoUserSessions']>[number]
>;

const ACTIVE_SESSION_WINDOW_SEC = 360;

export async function createLoginSession(variables: LoginCognitoUserMutationVariables): Promise<{ sessionId: number }> {
  const result = (await API.graphql(
    graphqlOperation(loginCognitoUser, variables)
  )) as GraphQLResult<LoginCognitoUserMutation>;

  const sessionId = result.data?.loginCognitoUser?.sessionid;
  if (sessionId == null) {
    throw new Error('Failed to create login session');
  }

  return { sessionId };
}

export async function getCognitoUserSessionsList(): Promise<CognitoUserSession[]> {
  const result = (await API.graphql(
    graphqlOperation(getCognitoUserSessions)
  )) as GraphQLResult<GetCognitoUserSessionsQuery>;

  return (result.data?.getCognitoUserSessions ?? []).filter(
    (session): session is CognitoUserSession => session != null
  );
}

export function getActiveCognitoSessions(sessions: CognitoUserSession[], nowSec = Date.now() / 1000): CognitoUserSession[] {
  return sessions.filter((session) => {
    const loginTimestamp = Number(session.logintimestamp);
    return loginTimestamp - nowSec > -ACTIVE_SESSION_WINDOW_SEC;
  });
}

export async function logoutCognitoSessions(): Promise<void> {
  await API.graphql(graphqlOperation(logoutCognitoUser)) as GraphQLResult<LogoutCognitoUserMutation>;
}

export async function globalSignOut(accessToken: string): Promise<void> {
  await axios({
    method: 'POST',
    url: API_ENDPOINTS.REACT_APP_GLOBAL_SIGNOUT_URL,
    headers: { Authorization: accessToken },
  });
}

export async function signInWithGoogle(): Promise<void> {
  await Auth.federatedSignIn({ provider: CognitoHostedUIIdentityProvider.Google });
}
